import { getDocs, query, where } from "firebase/firestore";
import { db } from "@freetech/models/db";
import { List, Card } from "@freetech/models/freelo";
import { getCurrentUserId } from "@/core/auth/getCurrentUserId";
import { getAuth } from "firebase/auth";
import { NestedBoard } from "types/freelo/nested";

export const getBoards = async (): Promise<NestedBoard[]> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error("User not found");
    }

    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const tokenResult = await currentUser.getIdTokenResult();
    const userRole = tokenResult.claims.role as string;

    if (userRole === "admin") {
      const boardsRef = db.boards;
      const allBoardIds = new Set<string>();
      const boardData = new Map<string, any>();

      const boardsSnapshot = await getDocs(boardsRef);
      boardsSnapshot.docs.forEach(doc => {
        allBoardIds.add(doc.id);
        boardData.set(doc.id, doc.data());
      });

      return boardsSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        lists: [],
        cards: [],
      }));
    }

    const boardsRef = db.boards;
    const allBoardIds = new Set<string>();
    const boardData = new Map<string, any>();

    const queries = [
      getDocs(query(boardsRef, where("readUserIds", "array-contains", userId))),
      getDocs(query(boardsRef, where("writeUserIds", "array-contains", userId))),
      getDocs(query(boardsRef, where("createdBy", "==", userId))),
    ];

    if (userRole === "student") {
      queries.push(getDocs(query(boardsRef, where("associatedStudentIds", "array-contains", userId))));
    }

    try {
      const snapshots = await Promise.all(queries);
      snapshots.forEach(snapshot => {
        snapshot.docs.forEach(doc => {
          allBoardIds.add(doc.id);
          boardData.set(doc.id, doc.data());
        });
      });
    } catch (error) {
      console.warn("Board queries failed:", error);
    }

    if (userRole === "stakeholder" || userRole === "freelancer") {
      try {
        const clientsRef = db.clients;
        const associatedField = userRole === "stakeholder" ? "associatedStakeholderIds" : "associatedFreelancerIds";

        const clientSnapshot = await getDocs(query(clientsRef, where(associatedField, "array-contains", userId)));

        for (const clientDoc of clientSnapshot.docs) {
          try {
            const clientBoardsSnapshot = await getDocs(query(boardsRef, where("clientId", "==", clientDoc.id)));

            clientBoardsSnapshot.docs.forEach(doc => {
              allBoardIds.add(doc.id);
              boardData.set(doc.id, doc.data());
            });
          } catch (clientBoardError) {
            console.warn(`Error fetching boards for client ${clientDoc.id}:`, clientBoardError);
          }
        }
      } catch (error) {
        console.warn("Client association queries failed:", error);
      }
    }

    if (allBoardIds.size === 0) {
      return [];
    }

    const boards = await Promise.all(
      Array.from(allBoardIds).map(async boardId => {
        try {
          const [listsSnapshot, cardsSnapshot] = await Promise.all([
            getDocs(db.lists(boardId)),
            getDocs(db.cards(boardId)),
          ]);

          const lists = listsSnapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
          })) as List[];

          const allCards = cardsSnapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
            boardId: boardId,
          })) as Card[];

          return {
            ...boardData.get(boardId),
            lists: lists,
            cards: allCards,
            id: boardId,
          } as NestedBoard;
        } catch (error) {
          console.error(`Error fetching board ${boardId}:`, error);
          return {
            ...boardData.get(boardId),
            lists: [],
            cards: [],
            id: boardId,
          } as NestedBoard;
        }
      })
    );

    return boards.filter(board => board !== null);
  } catch (error) {
    console.error("Error fetching boards:", error);

    if ((error as any)?.code === "permission-denied") {
      return [];
    }

    throw error;
  }
};
